import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Icon, LoadingDots } from '@advanza/ui'
import ListKeyboardNav from '@trustoo/components/ui/ListKeyboardNav'
import clsx from 'clsx'
import ProSearchResult from 'components/misc/ProSearchResult'
import { getActiveStatus } from 'components/providers/_providerActiveStatus'
import { useOutsideClick } from 'hooks/miscHooks'
import { KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import style from './searchPro.module.css'

const SEARCH_DEBOUNCE_MS = 250

const useSearch = (serviceId?: number) => {
    const [isLoading, setIsLoading] = useState(false)
    const initialState = {
        providers: [],
        providerWithInvoice: null,
    }
    const [results, setResults] = useState<any>(initialState)
    const [enableSearchOnChange, setEnableSearchOnChange] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => () => clearTimeout(debounceRef.current), [])

    const clear = () => {
        clearTimeout(debounceRef.current)
        setResults(initialState)
        setEnableSearchOnChange(false)
    }

    const runSearch = async (search: string) => {
        setIsLoading(true)
        try {
            const { results } = await call('office/top-input-search/search', {
                json: { search, serviceId },
            })
            setResults(results)
            setEnableSearchOnChange(true)
        } catch (error) {
            console.error('Top input search failed', error)
        } finally {
            setIsLoading(false)
        }
    }

    const onSearch = (e: KeyboardEvent<HTMLInputElement>) => {
        const search = (e.target as HTMLInputElement).value

        if (search.length < 2) {
            clear()
            return
        }

        if (e.key !== 'Enter' && !enableSearchOnChange) {
            return
        }

        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => runSearch(search), SEARCH_DEBOUNCE_MS)
    }

    return {
        results,
        onSearch,
        isLoading,
        clear,
    }
}

interface Props {
    onSelect?: (provider: any) => void
    serviceId?: number
}

const SearchProOrInvoice = ({ onSelect, serviceId }: Props) => {
    const { clear, isLoading, onSearch, results } = useSearch(serviceId)
    const ref = useRef(null)
    const inputRef = useRef<any>(null)
    useOutsideClick(ref, clear)

    const onClick = (e: MouseEvent<HTMLAnchorElement>, pro: any) => {
        if (onSelect) {
            onSelect(pro)
            e.preventDefault()
        }

        clear()
    }

    return (
        <div ref={ref} className={clsx(style.root, results.length && style.hasResults)}>
            <InputField
                controlled={false}
                ref={inputRef}
                inputPlaceholder="Enter query & press enter"
                before={isLoading ? <LoadingDots color="grey" /> : <Icon name="search" grey />}
                onKeyUp={onSearch}
            />
            {(results.providers.length > 0 || results.providerWithInvoice || isLoading) && (
                <div className={clsx(style.results, isLoading && style.loading)}>
                    <ListKeyboardNav onOtherKeyDown={() => inputRef.current?.doFocus()}>
                        {results.providers.map((result: any, i: number) => {
                            const status = getActiveStatus(result)
                            return (
                                <Link
                                    to={`/service/providers/provider/${result.service_provider_id}/company`}
                                    key={i}
                                    onClick={(e) => onClick(e, result)}
                                    className={style.result}>
                                    <ProSearchResult
                                        status={status as any}
                                        providerId={result.service_provider_id}
                                        businessName={result.business_name}
                                        city={result.city}
                                        avatarFileName={result.avatarFileName}
                                        userId={result.userId}
                                    />
                                </Link>
                            )
                        })}
                        {results.providerWithInvoice && (
                            <Link
                                className={style.result}
                                to={`/billing/invoices?query=${results.providerWithInvoice.invoices[0].invoice_nr}&aboutMonth=`}>
                                <ProSearchResult
                                    status={getActiveStatus(results.providerWithInvoice) as any}
                                    providerId={results.providerWithInvoice.service_provider_id}
                                    businessName={results.providerWithInvoice.business_name}
                                    city={results.providerWithInvoice.city}
                                    avatarFileName={results.providerWithInvoice.avatarFileName}
                                    userId={results.providerWithInvoice.userId}
                                />
                            </Link>
                        )}
                    </ListKeyboardNav>
                </div>
            )}
        </div>
    )
}

export default SearchProOrInvoice
